import type { AgentLeaderboardEntry } from '../schemas/agent-leaderboard.js';

/**
 * Cohort and personal-best framing for the agent leaderboard (gap G22).
 *
 * A single global top-50 demotivates almost everyone it shows. An agent in
 * their first month is ranked against people who have been selling for a year,
 * sees a number far down a list they will not climb this quarter, and stops
 * looking. The board then measures tenure and reports it as performance.
 *
 * Two framings fix that without inventing new data:
 *
 *   - COHORT — rank an agent among people who started around when they did.
 *     "3rd of 11 who started in August" is a race they can actually win.
 *   - PERSONAL BEST — rank them against their own past. This is the only frame
 *     that still works for the agent who is genuinely last in every cohort,
 *     which is exactly the agent most likely to quit.
 *
 * Pure and total; no I/O, no clock. The caller supplies the week.
 */

/** An agent's standing among people who started at the same time. */
export interface CohortStanding {
  /** `YYYY-MM` the agent joined — the cohort key. */
  readonly cohortMonth: string;
  /** 1-based position within the cohort. */
  readonly rank: number;
  /** How many agents share this cohort. */
  readonly cohortSize: number;
  /**
   * True when the cohort is too small for a rank to mean anything. Being
   * "1st of 1" reads as a joke, and telling someone they are last of two is
   * worse than telling them nothing.
   */
  readonly tooSmallToRank: boolean;
}

/** The minimum cohort where a rank carries information rather than noise. */
export const MIN_COHORT_SIZE = 4;

/** `YYYY-MM` from an ISO-ish timestamp; empty string when unusable. */
export function cohortMonthOf(joinedAtMs: number): string {
  if (!Number.isFinite(joinedAtMs) || joinedAtMs <= 0) return '';
  const d = new Date(joinedAtMs);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * Rank one agent among their cohort.
 *
 * Ties share the lower rank — two agents on 5 signups are both 2nd, and the
 * next is 4th. Standard competition ranking: splitting a tie arbitrarily tells
 * one of them they are behind someone they matched.
 */
export function cohortStanding(
  agentId: string,
  cohortMonth: string,
  cohort: readonly Pick<AgentLeaderboardEntry, 'agentId' | 'signupCount'>[],
): CohortStanding | null {
  const members = cohort.filter((m) => m.agentId);
  const me = members.find((m) => m.agentId === agentId);
  if (!me) return null;

  const ahead = members.filter((m) => m.signupCount > me.signupCount).length;

  return {
    cohortMonth,
    rank: ahead + 1,
    cohortSize: members.length,
    tooSmallToRank: members.length < MIN_COHORT_SIZE,
  };
}

/** An agent measured against their own history. */
export interface PersonalBest {
  /** Signups this week. */
  readonly current: number;
  /** The best week they have ever had, including this one. */
  readonly best: number;
  /** True when this week IS the best — the moment worth celebrating. */
  readonly isBest: boolean;
  /**
   * How far off their best they are. Zero when they have matched or beaten it.
   * The gap, not the rank, is the number an agent can act on this week.
   */
  readonly shortOfBestBy: number;
}

/**
 * Compare this week against every previous week.
 *
 * A first-ever week counts as a personal best. There is no honest alternative —
 * it is their best week so far — and it hands a new agent a win on day one,
 * which is precisely when the leaderboard would otherwise tell them they are
 * 47th.
 */
export function personalBest(
  currentWeekSignups: number,
  previousWeekSignups: readonly number[],
): PersonalBest {
  const current =
    Number.isFinite(currentWeekSignups) && currentWeekSignups > 0
      ? Math.trunc(currentWeekSignups)
      : 0;
  const history = previousWeekSignups.filter((n) => Number.isFinite(n) && n >= 0).map(Math.trunc);
  const priorBest = history.length ? Math.max(...history) : 0;
  const best = Math.max(current, priorBest);

  return {
    current,
    best,
    isBest: current >= priorBest,
    shortOfBestBy: Math.max(0, priorBest - current),
  };
}
